/**
 * Elder selector — top of the sidebar. Shows the currently-selected
 * elder with a chevron; click to switch elders or add a new one via the
 * "Add another person" dialog. The data model supports N elders per
 * caregiver (see docs/v1-ui-deferrals.md).
 */

import * as RadixDropdown from '@radix-ui/react-dropdown-menu';
import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { accountSwitchTarget } from '@/lib/accountSwitchTarget';
import { useMyMemberships } from '@/hooks/useMyMemberships';
import { useAuth } from '@/hooks/useAuth';
import { AddPersonDialog } from '@/components/AddPersonDialog';

export function EldersDropdown() {
  const auth = useAuth();
  const params = useParams<{ aid?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const uid = auth.state === 'signedIn' ? auth.user.uid : null;
  const { memberships } = useMyMemberships(uid);
  const [addOpen, setAddOpen] = useState(false);

  const activeAid = params.aid ?? memberships[0]?.id;
  const active = memberships.find((m) => m.id === activeAid) ?? memberships[0];

  if (!active) return null;

  return (
    <>
    <RadixDropdown.Root>
      <RadixDropdown.Trigger
        className={cn(
          'flex w-full items-center justify-between rounded-md border border-paper-border',
          'bg-white px-3 py-2 text-left text-sm font-medium text-ink',
          'hover:bg-paper-alt focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500',
        )}
      >
        <span className="truncate">{active.accountName || 'Unnamed person'}</span>
        <ChevronDown />
      </RadixDropdown.Trigger>
      <RadixDropdown.Portal>
        <RadixDropdown.Content
          align="start"
          sideOffset={4}
          className="z-50 min-w-[14rem] rounded-md border border-paper-border bg-white p-1 shadow-md"
        >
          {memberships.map((m) => (
            <RadixDropdown.Item
              key={m.id}
              className={cn(
                'cursor-pointer rounded px-3 py-2 text-sm outline-none',
                'data-[highlighted]:bg-accent-50 data-[highlighted]:text-accent-700',
                m.id === active.id && 'font-medium text-accent-700',
              )}
              onSelect={() => navigate(accountSwitchTarget(location.pathname, params.aid, m.id))}
            >
              {m.id === active.id ? '✓ ' : ''}
              {m.accountName || 'Unnamed person'}
            </RadixDropdown.Item>
          ))}
          <RadixDropdown.Separator className="my-1 h-px bg-paper-border" />
          <RadixDropdown.Item
            className={cn(
              'cursor-pointer rounded px-3 py-2 text-sm text-ink outline-none',
              'data-[highlighted]:bg-accent-50 data-[highlighted]:text-accent-700',
            )}
            onSelect={() => setAddOpen(true)}
          >
            + Add another person
          </RadixDropdown.Item>
        </RadixDropdown.Content>
      </RadixDropdown.Portal>
    </RadixDropdown.Root>
    <AddPersonDialog open={addOpen} onOpenChange={setAddOpen} />
    </>
  );
}

function ChevronDown() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <path d="M4 6l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
